import api from '../http.service';
import { API_ENDPOINTS } from '@/config/api.config';

const BASE = API_ENDPOINTS.STUDIO.ANNOUNCEMENTS;

export type AnnouncementPriority = 'low' | 'medium' | 'high' | 'critical';
export type AnnouncementCategory = 'general' | 'technical' | 'support' | 'other';
export type AnnouncementType = 'platform' | 'global' | 'creator' | 'agent';

export interface Announcement {
  id: string;
  tenant_id: string | null;
  created_by: string;
  title: string;
  message: string;
  priority: AnnouncementPriority | string;
  category: AnnouncementCategory | string;
  announcement_type: AnnouncementType | string;
  is_active: boolean;
  expires_at: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementManagementResult {
  items: Announcement[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateAnnouncementPayload {
  title: string;
  message: string;
  priority?: AnnouncementPriority;
  category?: AnnouncementCategory;
  announcementType: AnnouncementType;
  expiresAt?: string | null;
  /** Required for superadmin when `announcementType` is not `platform`. */
  tenantId?: string;
}

export interface UpdateAnnouncementPayload {
  title?: string;
  message?: string;
  priority?: AnnouncementPriority;
  category?: AnnouncementCategory;
  isActive?: boolean;
  expiresAt?: string | null;
}

export const listActive = async (): Promise<Announcement[]> => {
  const response = await api.get(BASE);
  return response.data.data as Announcement[];
};

export interface ListForManagementParams {
  tenantId?: string;
  type?: string;
  isActive?: boolean | string;
  limit?: number;
  offset?: number;
}

export const listForManagement = async (
  params?: ListForManagementParams,
): Promise<AnnouncementManagementResult> => {
  const response = await api.get(`${BASE}/manage`, { params });
  return response.data.data as AnnouncementManagementResult;
};

export const createAnnouncement = async (
  data: CreateAnnouncementPayload,
): Promise<Announcement> => {
  const response = await api.post(BASE, data);
  return response.data.data as Announcement;
};

export const updateAnnouncement = async (
  id: string,
  data: UpdateAnnouncementPayload,
): Promise<Announcement> => {
  const response = await api.patch(`${BASE}/${id}`, data);
  return response.data.data as Announcement;
};

export const deleteAnnouncement = async (id: string): Promise<void> => {
  await api.delete(`${BASE}/${id}`);
};

export const dismissAnnouncement = async (id: string): Promise<void> => {
  await api.post(`${BASE}/${id}/dismiss`);
};

export default {
  listActive,
  listForManagement,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  dismissAnnouncement,
};
